/**
 * Firebase Auth の状態を購読し、admins コレクションからロール情報を読み込む。
 * 未登録ユーザーは自動的にサインアウトされ、null を返す。
 * 仕様書:
 *   - docs/01_spec/spec_common.md C-2.1, C-2.2 （旧仕様）
 *   - docs/01_spec/spec_user_management.md （v2 拡張: dept / level / isOwner / status）
 */
import {
  GoogleAuthProvider,
  onIdTokenChanged,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { doc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { safeUpdateDoc } from '@/lib/safeWrite';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { AdminStatus, DepartmentId, Level, Role } from '@goenhub/shared';

import { auth, db, functions } from '@/lib/firebase';

const acceptInvitationFn = httpsCallable<
  Record<string, never>,
  { ok: boolean; role: Role; dept?: DepartmentId; level?: Level; isOwner?: boolean; alreadyRegistered: boolean }
>(functions, 'acceptInvitation');

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  /** 後方互換: v1 ロール。 */
  role: Role;
  /** v2: 部署 ID。未マイグレーション admin は 'admin' をフォールバック。 */
  dept: DepartmentId;
  /** v2: 権限レベル。未マイグレーション admin は role からマップ。 */
  level: Level;
  /** v2: 最高権限フラグ。 */
  isOwner: boolean;
  /** v2: アカウント状態。 */
  status: AdminStatus;
  uiTheme: 'dark' | 'light' | 'system';
  sidebarCollapsed: boolean;
}

type AuthStatus = 'loading' | 'signed-out' | 'unregistered' | 'signed-in';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  firebaseUser: User | null;
  signIn: () => Promise<void>;
  signOutUser: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const loginStampedUids = new Set<string>();

/** role → (dept, level, isOwner) フォールバックマッピング（未マイグレーション admin 用）。 */
function fallbackFromRole(role: Role): { dept: DepartmentId; level: Level; isOwner: boolean } {
  if (role === 'owner') return { dept: 'admin', level: 'leader', isOwner: true };
  if (role === 'editor') return { dept: 'admin', level: 'employee', isOwner: false };
  return { dept: 'admin', level: 'external', isOwner: false };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsub = onIdTokenChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);
      if (!fbUser) {
        setUser(null);
        setStatus('signed-out');
        return;
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!firebaseUser) return;
    const ref = doc(db, 'admins', firebaseUser.uid);
    const unsub = onSnapshot(
      ref,
      async (snap) => {
        if (!snap.exists()) {
          try {
            const result = await acceptInvitationFn({});
            if (result.data.ok) {
              // acceptInvitation 内で Custom Claims を即座セット済み。
              // ID トークンを強制リフレッシュして新しい Claims を反映させる。
              await firebaseUser.getIdToken(true);
              return;
            }
          } catch {
            // 招待なし → サインアウト
          }
          setStatus('unregistered');
          setError('このアカウントは管理者として登録されていません。オーナーに連絡してください。');
          await signOut(auth).catch(() => undefined);
          setUser(null);
          return;
        }
        const data = snap.data();
        const role = (data?.role ?? 'viewer') as Role;
        const fallback = fallbackFromRole(role);
        const dept: DepartmentId = (data?.dept as DepartmentId | undefined) ?? fallback.dept;
        const level: Level = (data?.level as Level | undefined) ?? fallback.level;
        const isOwner: boolean = data?.isOwner !== undefined ? Boolean(data.isOwner) : fallback.isOwner;
        const accStatus: AdminStatus = (data?.status as AdminStatus | undefined) ?? 'active';

        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          role,
          dept,
          level,
          isOwner,
          status: accStatus,
          uiTheme: data?.uiTheme ?? 'dark',
          sidebarCollapsed: data?.sidebarCollapsed ?? false,
        });
        setStatus('signed-in');
        setError(null);
      },
      (err) => {
        console.error('[useAuth] admins snapshot error:', err);
        setError('管理者情報の取得に失敗しました。');
      },
    );
    return () => unsub();
  }, [firebaseUser]);

  useEffect(() => {
    if (status !== 'signed-in' || !firebaseUser) return;
    const uid = firebaseUser.uid;
    if (loginStampedUids.has(uid)) return;
    loginStampedUids.add(uid);
    safeUpdateDoc(doc(db, 'admins', uid), { lastLoginAt: serverTimestamp() }).catch(() => undefined);
  }, [status, firebaseUser]);

  const signIn = useCallback(async () => {
    setError(null);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    try {
      await signInWithPopup(auth, provider);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setError(`サインインに失敗しました: ${message}`);
      throw err;
    }
  }, []);

  const signOutUser = useCallback(async () => {
    await signOut(auth);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, firebaseUser, signIn, signOutUser, error }),
    [status, user, firebaseUser, signIn, signOutUser, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
